import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, CheckCircle2, Lock, Zap, Package, Eye, Play
} from 'lucide-react';
import { ErrorBanner } from '../components/ErrorBanner';
import { SegmentedProgress } from '../components/SegmentedProgress';
import { getStepMeta, PHASES } from '../lib/stepIcons';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PATH_INFO = {
  path_fast_cash: { name: 'Fast Cash Path', icon: Zap, color: '#10B981' },
  path_digital_product: { name: 'Simple Digital Product Path', icon: Package, color: '#8B5CF6' },
  path_faceless_affiliate: { name: 'Faceless Affiliate Path', icon: Eye, color: '#F59E0B' }
};

const PathView = () => {
  const { pathId } = useParams();
  const { user, currentPath, getAuthHeaders } = useAuth();
  const [pathData, setPathData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [pathRes, stepsRes, progressRes] = await Promise.all([
        fetch(`${API}/paths/${pathId}`),
        fetch(`${API}/paths/${pathId}/steps`),
        fetch(`${API}/progress/me/${pathId}`, { headers: getAuthHeaders() })
      ]);

      if (progressRes.status === 401) {
        setError({ message: 'Your session has expired. Please sign in again.', isAuth: true });
        return;
      }

      if (!pathRes.ok || !stepsRes.ok) {
        setError({ message: 'Failed to load path details. The server may be temporarily unavailable.' });
        return;
      }

      setPathData(await pathRes.json());
      setSteps(await stepsRes.json());
      if (progressRes.ok) setProgress(await progressRes.json());
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Error fetching path data:', err);
      setError({ message: 'Could not connect to the server. Check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  }, [pathId, getAuthHeaders]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const completedSteps = progress.filter(p => p.is_completed).length;
  const progressPercentage = steps.length > 0 ? (completedSteps / steps.length) * 100 : 0;
  const currentStepNumber = currentPath?.current_step_number || 1;

  const pathInfo = PATH_INFO[pathId] || {};
  const PathIcon = pathInfo.icon || Zap;

  const getStepStatus = (step) => {
    const stepProgress = progress.find(p => p.step_id === step.id);
    if (stepProgress?.is_completed) return 'completed';
    if (step.step_number === currentStepNumber) return 'current';
    if (step.step_number < currentStepNumber) return 'unlocked';
    return 'locked';
  };

  const isStepAccessible = (stepNumber) => stepNumber <= currentStepNumber;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  if (error) {
    return (
      <AppLayout maxWidth="max-w-5xl">
        <ErrorBanner
          message={error.message}
          isAuthError={error.isAuth}
          onRetry={!error.isAuth ? () => { setLoading(true); fetchData(); } : undefined}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout maxWidth="max-w-5xl">
      {/* Back link */}
      <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors" data-testid="back-to-dashboard">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Dashboard
      </Link>

      {/* Path Header */}
      {pathData && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${pathInfo.color}15` }}
            >
              <PathIcon className="w-5 h-5" style={{ color: pathInfo.color }} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground" data-testid="path-title">
                {pathData.name}
              </h1>
              <p className="text-sm text-muted-foreground">{pathData.description}</p>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-3.5 border border-slate-200">
            <div className="flex items-center justify-between text-xs mb-2.5">
              <span className="text-muted-foreground font-medium">Overall Progress</span>
              <span className="font-semibold text-foreground">{completedSteps} of {steps.length} complete</span>
            </div>
            <SegmentedProgress
              steps={steps}
              progress={progress}
              currentStepNum={currentStepNumber}
              pathId={pathId}
              showLabels={false}
            />
          </div>
        </motion.div>
      )}

      {/* Steps grouped by phase, in a 2-col grid */}
      <div className="space-y-8">
        {PHASES.map((phase) => {
          const phaseSteps = steps.filter((s) => phase.steps.includes(s.step_number));
          if (phaseSteps.length === 0) return null;
          return (
            <section key={phase.id} data-testid={`phase-section-${phase.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl" aria-hidden="true">{phase.emoji}</span>
                <h2 className="text-lg font-bold tracking-tight text-foreground">{phase.label}</h2>
                <div className="flex-1 h-px bg-slate-200" />
                <span
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: phase.color }}
                >
                  Steps {phase.steps[0]}–{phase.steps[phase.steps.length - 1]}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {phaseSteps.map((step, idx) => {
                  const status = getStepStatus(step);
                  const accessible = isStepAccessible(step.step_number);
                  const meta = getStepMeta(step.step_number);
                  const Icon = meta.icon;

                  return (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Card
                        className={`relative overflow-hidden h-full transition-all ${
                          status === 'completed'
                            ? 'border-teal-500/60 bg-white'
                            : status === 'current'
                              ? 'border-teal-500/60 bg-white shadow-[0_4px_24px_rgba(14,129,117,0.18)]'
                              : !accessible
                                ? 'border-slate-200 bg-slate-50/60 opacity-70'
                                : 'border-slate-200 bg-white hover:border-teal-500/40'
                        }`}
                        data-testid={`step-card-${step.step_number}`}
                      >
                        {status === 'completed' && (
                          <div className="creataby-done-ribbon" data-testid={`done-ribbon-${step.step_number}`}>
                            ✓ Done
                          </div>
                        )}

                        <CardContent className="py-4">
                          <div className="flex items-start gap-3">
                            {/* Icon square */}
                            <div
                              className={`w-10 h-10 rounded-lg bg-gradient-to-br ${meta.gradient} flex items-center justify-center flex-shrink-0 shadow-sm ${
                                !accessible ? 'grayscale opacity-60' : ''
                              }`}
                            >
                              {!accessible ? (
                                <Lock className="text-white" size={16} />
                              ) : (
                                <Icon className="text-white" size={20} strokeWidth={2.2} />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className="text-[10px] uppercase tracking-widest font-bold"
                                  style={{ color: accessible ? meta.accent : '#94a3b8' }}
                                >
                                  Step {step.step_number}
                                </span>
                                {status === 'current' && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">
                                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 creataby-pulse-dot" />
                                    In Progress
                                  </span>
                                )}
                                {status === 'completed' && (
                                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                                    Done
                                  </span>
                                )}
                                {!accessible && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                                    <Lock className="h-2.5 w-2.5" />
                                    Locked
                                  </span>
                                )}
                              </div>
                              <h3 className="font-semibold text-foreground text-sm leading-snug mb-1">{step.title}</h3>
                              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{step.description}</p>
                              {step.has_ai_generator && accessible && (
                                <span className="inline-flex items-center text-[10px] text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded">
                                  <Zap className="h-3 w-3 mr-1" />
                                  Creataby guided
                                </span>
                              )}
                            </div>
                          </div>

                          {accessible && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <Link to={`/path/${pathId}/step/${step.step_number}`}>
                                <Button
                                  variant={status === 'current' ? 'default' : 'outline'}
                                  size="sm"
                                  className={`text-xs w-full ${
                                    status === 'current'
                                      ? 'bg-creataby-gradient-btn text-white hover:opacity-95 font-semibold'
                                      : ''
                                  }`}
                                >
                                  {status === 'completed' ? 'Review' : status === 'current' ? (
                                    <><Play className="h-3.5 w-3.5 mr-1" />Start</>
                                  ) : 'View'}
                                </Button>
                              </Link>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </AppLayout>
  );
};

export default PathView;
